import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert, Box, Button, CircularProgress, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { fetchAhaIssuanceReport } from "../api/apiClient";
import AhaStatusChip from "../components/aha/AhaStatusChip";
import { errorCodeText, formatCompletionDate } from "../components/aha/ahaText";

const STATES = [
  { value: "", label: "All states" },
  { value: "ASSIGNED", label: "Card issued" },
  { value: "UNKNOWN", label: "Result unknown" },
  { value: "REJECTED", label: "Rejected" },
  { value: "PENDING", label: "Queued" },
  { value: "SENDING", label: "Sending" },
];

const COLUMNS = [
  ["studentName", "Student"], ["classTitle", "Class"], ["courseNumber", "Course #"], ["productNumber", "Product"],
  ["state", "State"], ["reason", "Reason"], ["completionDate", "Completion date"], ["instructorName", "Attested by"],
  ["attestedAt", "Attested at"], ["environment", "Environment"],
];

/** Spreadsheet-safe CSV cell: quoted, and a leading = + - @ can't be read as a formula. */
function csvCell(value) {
  let text = value == null ? "" : String(value);
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
}

export function toCsv(rows) {
  const header = COLUMNS.map(([, label]) => csvCell(label)).join(",");
  const lines = rows.map((row) => COLUMNS.map(([key]) => csvCell(
    key === "reason" ? errorCodeText(row.errorCode) || "" : row[key])).join(","));
  return [header, ...lines].join("\r\n");
}

/**
 * Every AHA eCard request in the center (coordinators/admins): who, which class and product, who attested, and the
 * current state, with filters and a CSV export. Card codes and claim links are never part of this report.
 */
export default function AhaIssuedCardsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [state, setState] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    let active = true;
    fetchAhaIssuanceReport()
      .then((data) => { if (active) setRows(Array.isArray(data) ? data : []); })
      .catch(() => { if (active) { setRows([]); setError("Unable to load AHA eCard requests."); } });
    return () => { active = false; };
  }, []);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (rows || []).filter((r) => (!state || r.state === state)
      && (!q || [r.studentName, r.classTitle, r.courseNumber, r.instructorName].some((v) => String(v || "").toLowerCase().includes(q))));
  }, [rows, state, search]);

  const exportCsv = () => {
    const blob = new Blob([toCsv(shown)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `aha-ecards-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 2, md: 4 }, py: { xs: 3, md: 5 }, fontFamily: "var(--font-ui)" }}>
      <Typography component="h1" sx={{ fontFamily: "var(--font-display)", fontSize: { xs: 26, md: 32 }, fontWeight: 600 }}>
        AHA eCards
      </Typography>
      <Typography sx={{ color: "var(--color-accent-violet-mid)", fontSize: 14, mt: 0.5, mb: 3 }}>
        Every AHA eCard request in your training center. Card codes stay with the student.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2, alignItems: "center" }}>
        <TextField size="small" label="Search student, class or instructor" value={search}
          onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 280 }} />
        <TextField size="small" select label="State" value={state} onChange={(e) => setState(e.target.value)} sx={{ minWidth: 180 }}>
          {STATES.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
        </TextField>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportCsv} disabled={!shown.length}>
          Export CSV
        </Button>
      </Box>
      {rows === null ? (
        <Box sx={{ display: "grid", placeItems: "center", p: 6 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid var(--color-hairline-cloud)", borderRadius: "12px" }}>
          <Table size="small" aria-label="AHA eCard requests">
            <TableHead>
              <TableRow>
                {["Student", "Class", "Product", "State", "Completion", "Attested by"].map((h) => (
                  <TableCell key={h} sx={{ fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.length === 0 && (
                <TableRow><TableCell colSpan={6} sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                  {rows.length ? "No requests match these filters." : "No AHA eCards have been requested yet."}
                </TableCell></TableRow>
              )}
              {shown.map((r) => (
                <TableRow key={r.id} hover sx={{ cursor: "pointer" }} onClick={() => navigate(`/course-hub/${r.classId}`)}>
                  <TableCell sx={{ fontWeight: 600 }}>{r.studentName || `Student ${r.studentId}`}</TableCell>
                  <TableCell>{r.classTitle || `Class ${r.classId}`}{r.courseNumber ? ` · ${r.courseNumber}` : ""}</TableCell>
                  <TableCell sx={{ fontFamily: "var(--font-mono)" }}>{r.productNumber}</TableCell>
                  <TableCell>
                    <AhaStatusChip item={{ issuanceState: r.state }} />
                    {errorCodeText(r.errorCode) && (
                      <Typography sx={{ fontSize: 11.5, color: "var(--color-accent-violet-mid)", mt: 0.5 }}>{errorCodeText(r.errorCode)}</Typography>
                    )}
                  </TableCell>
                  <TableCell>{formatCompletionDate(r.completionDate)}</TableCell>
                  <TableCell>{r.instructorName || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
